import React from 'react';

import { PlusIcon } from '@vlabs/icons';
import { Control, useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button/Button';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { CardId } from '@vlabs/api/point-api/model';

import { CreateCardForm, createCardFormSubmitHandler } from '../../entities/cards/create-card';
import type { TCreateCardFormData } from '../../entities/cards/create-card/types';

interface CreateCardButtonProps extends Omit<ButtonProps, 'onClick'> {
  onCreateCard?: (cardId: CardId) => void;
  defaultValues?: Partial<TCreateCardFormData>;
}

export function CreateCardButton({
  onCreateCard = undefined,
  defaultValues = undefined,
  ...props
}: CreateCardButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const createCardModal = useModal();

  return (
    <React.Fragment>
      {createCardModal.wrap(
        <CreateCardForm
          defaultValues={defaultValues}
          onCancel={createCardModal.close}
          onSubmit={async (...args) => {
            const cardId = await createCardFormSubmitHandler(...args);
            if (cardId === undefined) {
              toast.error(t('features:createCard.confirmation.error'));
              return;
            }
            createCardModal.close();
            toast.success(t('features:createCard.confirmation.success'));
            onCreateCard?.(cardId);
          }}
        />,
      )}

      <Control.Button
        icon={<PlusIcon />}
        onClick={createCardModal.open}
        {...props}
      >
        {props?.children ?? t('features:createCard.btn.add')}
      </Control.Button>
    </React.Fragment>
  );
}
